import { Button } from '@base-ui/react/button';
import { Input } from '@base-ui/react/input';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { BackLink, ForwardLink } from '~/components/ui/NavLinks';
import { PageTitle } from '~/components/ui/Page';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import { LANGUAGE_LABELS } from '~/constants/languages';
import buttonStyles from '~/styles/button.module.css';
import { CertificateTemplateControl } from './CertificateTemplateControl';

type SeriesAdmin = components['schemas']['SeriesAdmin'];

function invalidateSeries(seriesId: number) {
  queryClient.invalidateQueries({
    queryKey: api.adminGetSeries.getQueryKey({ path: { series_id: seriesId } }),
  });
  queryClient.invalidateQueries({
    queryKey: api.adminListSeries.getQueryKey({}),
  });
}

export const Route = createFileRoute('/_admin/series/$seriesId')({
  component: SeriesDetail,
});

function SeriesDetail() {
  const { seriesId } = Route.useParams();
  const seriesIdNum = Number(seriesId);
  const [editing, setEditing] = useState(false);

  const admin = api.adminGetSeries.useQuery({
    path: { series_id: seriesIdNum },
  });

  if (admin.isLoading) return <Message>Loading series...</Message>;
  if (admin.error || !admin.data)
    return <Message $error>Error loading series</Message>;

  const series = admin.data;
  const certificateTitle = series.certificate_template_title;

  return (
    <div>
      <BackLink>
        <Link to="/series">&larr; Back to Series</Link>
      </BackLink>

      <Header>
        <PageTitle>{series.title}</PageTitle>
        {!editing && (
          <Button
            className={buttonStyles.Button}
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        )}
      </Header>

      {editing ? (
        <SeriesEditor
          key={series.id}
          series={series}
          onDone={() => setEditing(false)}
        />
      ) : (
        <>
          <Section>
            <SectionTitle>Overview</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>ID</FieldLabel>
                <FieldValue>{series.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Language</FieldLabel>
                <FieldValue>{LANGUAGE_LABELS[series.language]}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Slug</FieldLabel>
                <FieldValue>{series.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Order</FieldLabel>
                <FieldValue>{series.order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Status</FieldLabel>
                <FieldValue>{series.status}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Self Paced</FieldLabel>
                <FieldValue>{series.is_self_paced ? 'Yes' : 'No'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Starter</FieldLabel>
                <FieldValue>{series.is_starter ? 'Yes' : 'No'}</FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <SectionTitle>Certificate</SectionTitle>
            <CertificateRow>
              <div>
                <FieldValue>
                  {certificateTitle !== null ? (
                    <>
                      Students who complete every course in this series earn{' '}
                      <CertificateName>{certificateTitle}</CertificateName>.
                    </>
                  ) : (
                    <>
                      No certificate. Finishing this series earns nothing,
                      however many of its courses a student completes.
                    </>
                  )}
                </FieldValue>
              </div>
              <CertificateTemplateControl
                owner={{ type: 'series', id: series.id }}
                hasTemplate={certificateTitle !== null}
                defaultTitle={series.title}
                onChanged={() => invalidateSeries(series.id)}
              />
            </CertificateRow>
          </Section>

          <ForwardLink>
            <Link to="/courses" search={{ series: series.id }}>
              View courses &rarr;
            </Link>
          </ForwardLink>
        </>
      )}
    </div>
  );
}

function SeriesEditor({
  series,
  onDone,
}: {
  series: SeriesAdmin;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(series.title);
  const [slug, setSlug] = useState(series.slug);
  const [status, setStatus] = useState(series.status);

  const updateMutation = api.adminUpdateSeries.useMutation(undefined, {
    onSuccess: () => {
      invalidateSeries(series.id);
      onDone();
    },
  });

  const trimmedTitle = title.trim();
  // Publishing is one-way (the server rejects published -> draft), so an
  // already-published series shows no status control at all.
  const isPublished = series.status === 'published';
  const willPublish = !isPublished && status === 'published';

  const handleSave = () => {
    if (!trimmedTitle || updateMutation.isPending) return;
    updateMutation.mutate({
      path: { series_id: series.id },
      body: { title: trimmedTitle, slug, status },
    });
  };

  return (
    <Section>
      <SectionTitle>Edit series</SectionTitle>
      <FormGrid>
        <FormRow>
          <FormLabel htmlFor="series-title">Title</FormLabel>
          <TextInput
            id="series-title"
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTitle(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="series-slug">Slug</FormLabel>
          <TextInput
            id="series-slug"
            value={slug}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSlug(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="series-status">Status</FormLabel>
          {isPublished ? (
            <PublishedNote>
              Published and live for students. Publishing can't be undone, so
              there's nothing to change here.
            </PublishedNote>
          ) : (
            <>
              <StatusSelect
                id="series-status"
                value={status}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setStatus(e.target.value as SeriesAdmin['status'])
                }
              >
                <option value="draft">draft</option>
                <option value="published">published</option>
              </StatusSelect>
              {willPublish && (
                <PublishWarning>
                  Publishing is permanent — a published series can't be returned
                  to draft, since that would remove it for students who are
                  partway through it.
                </PublishWarning>
              )}
            </>
          )}
        </FormRow>
      </FormGrid>

      {updateMutation.isError && (
        <Message $error>Could not save changes. Please try again.</Message>
      )}

      <Actions>
        <Button
          className={buttonStyles.Button}
          onClick={handleSave}
          disabled={updateMutation.isPending || !trimmedTitle}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button className={buttonStyles.Button} onClick={onDone}>
          Cancel
        </Button>
      </Actions>
    </Section>
  );
}

const Header = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  justify-content: space-between;
`;

const CertificateName = styled.strong`
  color: var(--grey-900);
  font-weight: 600;
`;

const CertificateRow = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  justify-content: space-between;
  padding: var(--inner-padding);
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: var(--inner-padding);
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const FormLabel = styled.label`
  color: var(--grey-700);
  font-size: 0.8125rem;
  font-weight: 600;
`;

const fieldStyles = `
  background-color: var(--grey-50);
  border: 1px solid var(--grey-300);
  border-radius: 0.375rem;
  box-sizing: border-box;
  color: var(--grey-900);
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  width: 100%;

  &:focus {
    outline: 2px solid var(--blue);
    outline-offset: -1px;
  }
`;

const TextInput = styled(Input)`
  ${fieldStyles}
`;

const StatusSelect = styled.select`
  ${fieldStyles}
`;

const PublishWarning = styled.p`
  background: color-mix(in srgb, var(--orange) 12%, transparent);
  border-left: 3px solid var(--orange);
  border-radius: 0.25rem;
  color: var(--grey-800);
  font-size: 0.8125rem;
  line-height: 1.4;
  margin: 0.5rem 0 0;
  padding: 0.5rem 0.75rem;
`;

const PublishedNote = styled.p`
  color: var(--grey-700);
  font-size: 0.8125rem;
  line-height: 1.4;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: var(--inner-padding);
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
